import React, { useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  IconButton,
  Icon,
  Spinner,
  Tooltip,
} from '@chakra-ui/react';
import { FaPlus, FaTrash, FaComments } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { iosColors } from '../../utils/iosColors';

const LunaConversationSidebar = ({
  conversations,
  activeConversationId,
  isLoading,
  isMobile = false,
  onNewConversation,
  onSelectConversation,
  onDeleteConversation,
}) => {
  const { t } = useTranslation();
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return t('dashboard.luna.today', "Aujourd'hui");
    } else if (diffDays === 1) {
      return t('dashboard.luna.yesterday', 'Hier');
    } else if (diffDays < 7) {
      return `${diffDays} ${t('dashboard.luna.daysAgo', 'jours')}`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const truncateText = (text, maxLength = 40) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  return (
    <Box
      w={['100%', '100%', '220px']}
      h="100%"
      bg="#FAFAFB"
      borderRight="1px solid"
      borderColor={iosColors.border}
      display="flex"
      flexDirection="column"
      overflow="hidden"
    >
      {/* New Conversation Button */}
      <Box p={3} borderBottom="1px solid" borderBottomColor={iosColors.border}>
        <Button
          leftIcon={<Icon as={FaPlus} boxSize={3} />}
          size="sm"
          w="100%"
          bg="#1C1C1E"
          color="white"
          borderRadius="10px"
          fontSize="xs"
          fontWeight="600"
          _hover={{ bg: '#3A3A3C' }}
          _active={{ bg: '#3A3A3C' }}
          onClick={onNewConversation}
        >
          {t('dashboard.luna.newConversation', 'Nouvelle conversation')}
        </Button>
      </Box>

      {/* Conversations List */}
      <VStack
        flex="1"
        overflowY="auto"
        spacing={0}
        align="stretch"
        p={2}
        sx={{
          '&::-webkit-scrollbar': {
            width: '4px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(107, 114, 128, 0.3)',
            borderRadius: '2px',
          },
        }}
      >
        {isLoading ? (
          <Box py={8} textAlign="center">
            <Spinner size="sm" color="gray.500" />
          </Box>
        ) : conversations.length === 0 ? (
          <Box py={8} textAlign="center">
            <Icon as={FaComments} boxSize={6} color={iosColors.textMuted} mb={2} />
            <Text fontSize="xs" color={iosColors.textMuted}>
              {t('dashboard.luna.noConversations', 'Aucune conversation')}
            </Text>
          </Box>
        ) : (
          conversations.map((conv) => (
            <Box
              key={conv._id}
              position="relative"
              role="group"
            >
              <Box
                p={2}
                borderRadius="8px"
                cursor="pointer"
                bg={activeConversationId === conv._id ? 'rgba(28, 28, 30, 0.06)' : 'transparent'}
                borderLeft={activeConversationId === conv._id ? "3px solid #1C1C1E" : '3px solid transparent'}
                _hover={{
                  bg: activeConversationId === conv._id ? 'rgba(28, 28, 30, 0.08)' : iosColors.inputBg,
                }}
                onClick={() => onSelectConversation(conv._id)}
                transition="all 0.15s ease"
              >
                <VStack align="stretch" spacing={0}>
                  <HStack justify="space-between" align="flex-start">
                    <Text
                      fontSize="xs"
                      fontWeight={activeConversationId === conv._id ? '700' : '500'}
                      color={activeConversationId === conv._id ? '#1C1C1E' : iosColors.text}
                      noOfLines={1}
                      flex="1"
                    >
                      {truncateText(conv.title, 25)}
                    </Text>
                    {conv.isLegacy && (
                      <Tooltip label={t('dashboard.luna.legacyConversation', 'Historique')}>
                        <Box
                          bg="#fbbf24"
                          borderRadius="full"
                          px={1}
                          py={0.5}
                        >
                          <Text fontSize="7px" fontWeight="600" color="#78350f">
                            LEGACY
                          </Text>
                        </Box>
                      </Tooltip>
                    )}
                  </HStack>
                  <Text fontSize="10px" color={iosColors.textMuted} noOfLines={1}>
                    {truncateText(conv.preview, 30)}
                  </Text>
                  <Text fontSize="9px" color={iosColors.textMuted} mt={0.5}>
                    {formatDate(conv.lastMessageAt)}
                  </Text>
                </VStack>
              </Box>

              {/* Delete: confirm inline or trash icon */}
              {!conv.isLegacy && confirmDeleteId === conv._id ? (
                <HStack
                  position="absolute"
                  top={0} left={0} right={0} bottom={0}
                  bg="white"
                  borderRadius="8px"
                  zIndex={10}
                  justify="center"
                  spacing={1}
                  px={2}
                >
                  <Text fontSize="10px" color={iosColors.text} fontWeight="500" flex={1}>
                    {t('dashboard.luna.confirmDelete', 'Supprimer ?')}
                  </Text>
                  <Button
                    size="xs"
                    bg={iosColors.red}
                    color="white"
                    borderRadius="md"
                    fontSize="10px"
                    h="22px"
                    px={2}
                    _hover={{ opacity: 0.9 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setConfirmDeleteId(null);
                      onDeleteConversation(conv._id);
                    }}
                  >
                    {t('common.delete', 'Oui')}
                  </Button>
                  <Button
                    size="xs"
                    variant="ghost"
                    color={iosColors.textMuted}
                    borderRadius="md"
                    fontSize="10px"
                    h="22px"
                    px={2}
                    onClick={(e) => {
                      e.stopPropagation();
                      setConfirmDeleteId(null);
                    }}
                  >
                    {t('common.cancel', 'Non')}
                  </Button>
                </HStack>
              ) : !conv.isLegacy && (
                <Box
                  as="button"
                  position="absolute"
                  top="50%"
                  right="6px"
                  transform="translateY(-50%)"
                  w="22px" h="22px"
                  borderRadius="6px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  opacity={isMobile ? 1 : 0}
                  bg="transparent"
                  color={iosColors.textMuted}
                  transition="all 0.15s"
                  zIndex={10}
                  sx={{
                    '[role=group]:hover &': { opacity: 1 },
                    '&:hover': { bg: `${iosColors.red}15`, color: iosColors.red },
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setConfirmDeleteId(conv._id);
                  }}
                >
                  <Icon as={FaTrash} boxSize={2.5} />
                </Box>
              )}
            </Box>
          ))
        )}
      </VStack>
    </Box>
  );
};

export default LunaConversationSidebar;
